/**
 * Simple Offline Manager using IndexedDB to store products and queued movements.
 */

const DB_NAME = 'StockProOffline';
const DB_VERSION = 5;

class OfflineManager {
    constructor() {
        this.db = null;
    }

    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains('products')) {
                    db.createObjectStore('products', { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains('purchaseOrders')) {
                    db.createObjectStore('purchaseOrders', { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains('movements')) {
                    db.createObjectStore('movements', { keyPath: 'tempId', autoIncrement: true });
                }
                if (!db.objectStoreNames.contains('queuedProducts')) {
                    db.createObjectStore('queuedProducts', { keyPath: 'tempId', autoIncrement: true });
                }
                if (!db.objectStoreNames.contains('receptions')) {
                    db.createObjectStore('receptions', { keyPath: 'tempId', autoIncrement: true });
                }
            };

            request.onsuccess = (event) => {
                this.db = event.target.result;
                resolve(this.db);
            };

            request.onerror = (event) => reject(event.target.error);
        });
    }

    async saveProducts(products) {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['products'], 'readwrite');
            const store = transaction.objectStore('products');

            // Clear existing for a fresh mirror
            store.clear();

            products.forEach(p => store.put(p));

            transaction.oncomplete = () => resolve();
            transaction.onerror = (event) => reject(event.target.error);
        });
    }

    async saveOrders(orders) {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['purchaseOrders'], 'readwrite');
            const store = transaction.objectStore('purchaseOrders');

            // On ne clear pas forcément tout, on met à jour les existantes
            orders.forEach(o => store.put(o));

            transaction.oncomplete = () => resolve();
            transaction.onerror = (event) => reject(event.target.error);
        });
    }

    async searchOrders(query, companyId, statusFilter = 'Tous') {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['purchaseOrders'], 'readonly');
            const store = transaction.objectStore('purchaseOrders');
            const request = store.getAll();

            request.onsuccess = () => {
                let results = request.result;

                if (companyId) results = results.filter(o => o.companyId === Number(companyId));

                // Default filter if no search is active
                if (!query && statusFilter === 'Tous') {
                    results = results.filter(o => ['Envoyée', 'Livrée', 'Partiellement Livrée'].includes(o.status));
                } else if (statusFilter !== 'Tous') {
                    results = results.filter(o => o.status === statusFilter);
                }

                if (!query) return resolve(results.slice(0, 50));

                const q = query.toLowerCase().trim();
                const filtered = results.filter(o =>
                    (o.reference?.toLowerCase().includes(q)) ||
                    (o.supplier?.name?.toLowerCase().includes(q))
                );

                resolve(filtered.slice(0, 50));
            };
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async searchProducts(query, companyId, warehouseId) {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['products'], 'readonly');
            const store = transaction.objectStore('products');
            const request = store.getAll();

            request.onsuccess = () => {
                let results = request.result;

                // Filter by company/warehouse if needed
                if (companyId) results = results.filter(p => p.companyId === Number(companyId));
                if (warehouseId) results = results.filter(p => p.warehouseId === Number(warehouseId));

                if (!query) return resolve(results);

                const q = query.toLowerCase().trim();
                const keywords = q.split(/\s+/);

                const filtered = results.filter(p => {
                    // Smart search logic: all keywords must match at least one field
                    return keywords.every(kw =>
                        (p.name?.toLowerCase().includes(kw)) ||
                        (p.sku?.toLowerCase().includes(kw)) ||
                        (p.barcode?.toLowerCase().includes(kw)) ||
                        (p.externalReference?.toLowerCase().includes(kw)) ||
                        (p.description?.toLowerCase().includes(kw))
                    );
                });

                // Prioritize exact matches on refs
                filtered.sort((a, b) => {
                    const aMatch = (a.sku === query || a.barcode === query || a.externalReference === query) ? 0 : 1;
                    const bMatch = (b.sku === query || b.barcode === query || b.externalReference === query) ? 0 : 1;
                    if (aMatch !== bMatch) return aMatch - bMatch;
                    return a.name.localeCompare(b.name);
                });

                resolve(filtered.slice(0, 20));
            };
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async queueMovement(movement) {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['movements'], 'readwrite');
            const store = transaction.objectStore('movements');
            const request = store.add({ ...movement, timestamp: new Date().toISOString() });

            request.onsuccess = () => resolve();
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async getQueuedMovements() {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['movements'], 'readonly');
            const store = transaction.objectStore('movements');
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async removeMovement(tempId) {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['movements'], 'readwrite');
            const store = transaction.objectStore('movements');
            const request = store.delete(tempId);
            request.onsuccess = () => resolve();
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async queueProduct(product) {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['queuedProducts'], 'readwrite');
            const store = transaction.objectStore('queuedProducts');
            const request = store.add({ ...product, timestamp: new Date().toISOString() });
            request.onsuccess = (event) => resolve(event.target.result); // Returns the tempId
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async getQueuedProducts() {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['queuedProducts'], 'readonly');
            const store = transaction.objectStore('queuedProducts');
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async removeQueuedProduct(tempId) {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['queuedProducts'], 'readwrite');
            const store = transaction.objectStore('queuedProducts');
            const request = store.delete(tempId);
            request.onsuccess = () => resolve();
            request.onerror = (event) => reject(event.target.error);
        });
    }

    // --- RECEPTIONS ---

    async queueReception(reception) {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['receptions'], 'readwrite');
            const store = transaction.objectStore('receptions');
            const request = store.add({ ...reception, timestamp: new Date().toISOString() });
            request.onsuccess = () => resolve();
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async getQueuedReceptions() {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['receptions'], 'readonly');
            const store = transaction.objectStore('receptions');
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async removeReception(tempId) {
        if (!this.db) await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['receptions'], 'readwrite');
            const store = transaction.objectStore('receptions');
            const request = store.delete(tempId);
            request.onsuccess = () => resolve();
            request.onerror = (event) => reject(event.target.error);
        });
    }

    async getQueueCount() {
        const [m, p, r] = await Promise.all([
            this.getQueuedMovements(),
            this.getQueuedProducts(),
            this.getQueuedReceptions()
        ]);
        return m.length + p.length + r.length;
    }
}

export default new OfflineManager();
